import React, {useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import ContainerLayout from '../../components/layout/ContainerLayout'
import useQueryData from '../../hooks/api/analysis/useQueryLotData'
import useGecko from '../../hooks/api/analysis/useQueryGecko'
import {CComposedChart, CComposedDataSet, IComposeChartProps} from './Chartsviewer'
import {CSVLink} from 'react-csv'
import {Data, LabelKeyObject} from 'react-csv/lib/core'
import WizButton from '../../components/button/WizButton'

interface IProps {
    className?: string
    Igroup?: string
    condition?: string
}

const ServerAnalysisContainer: React.FC<IProps> = ({Igroup, condition, className}) => {
    const [days] = useState(100)
    const [group] = useState(Igroup)
    const {data, refetch} = useQueryData(days, group)
    const {data: charts, refetch: rebit} = useGecko(days)

    const {
        dataset = [],
        title = '',
        headers = [],
        csvData = [],
    } = useMemo<IComposeChartProps>(() => {
        const dataset: CComposedDataSet[] = []
        const headers: LabelKeyObject[] = []
        const csvData: Data = []
        if (data === undefined || data.length === 0 || charts === undefined) {
            return {
                dataset,
                headers,
                csvData,
                title: group,
            }
        }
        Object.keys(data[0]).forEach(k => {
            if (k === 'trending' || k === '_id') {
                return
            }

            headers.push({
                label: k,
                key: k,
            })
        })
        headers.push(
            ...[
                {label: 'long_short_count_ratio', key: 'long_short_count_ratio'},
                {label: 'long_short_count_sum', key: 'long_short_count_sum'},
                {label: 'long_short_volume_ratio', key: 'long_short_volume_ratio'},
                {label: 'long_short_volume_sum', key: 'long_short_volume_sum'},
                {label: 'holding_long_short_volume_sum', key: 'holding_long_short_volume_sum'},
                {label: 'btc_alt_volume_ratio', key: 'btc_alt_volume_ratio'},
            ],
        )

        for (let key in data) {
            data[key].holding /= 200
            let longC = 0
            let shortC = 0
            let longV = 0
            let shortV = 0
            let btcV = 0
            let altV = 0
            let HLongV = 0
            let HShortV = 0

            let row = {}
            Object.keys(data[key]).forEach(k => {
                if (k === 'trending' || k === '_id') {
                    return
                }
                row[k] = data[key][k]
                if (k === 'long_count') {
                    longC = data[key][k]
                }
                if (k === 'short_count') {
                    shortC = data[key][k]
                }

                if (k === 'long_volume') {
                    longV = data[key][k]
                }
                if (k === 'short_volume') {
                    shortV = data[key][k]
                }

                if (k === 'holding_long_volume') {
                    HLongV = data[key][k]
                }
                if (k === 'holding_short_volume') {
                    HShortV = data[key][k]
                }

                if (k === 'holding_btc_volume') {
                    btcV = Math.abs(data[key][k])
                }
                if (k === 'holding_alt_volume') {
                    altV = Math.abs(data[key][k])
                }

                if (typeof data[key][k] === 'number') {
                    data[key][k] = parseFloat(data[key][k].toFixed(3))
                }
            })

            if (shortC === 0) {
                shortC = 1
            }
            if (shortV === 0) {
                shortV = 1
            }
            if (altV === 0) {
                altV = 1
            }
            let set: CComposedDataSet = {
                ...data[key],
                long_short_count_ratio: parseFloat((longC / shortC).toFixed(3)),
                long_short_count_sum: longC - shortC,

                long_short_volume_ratio: parseFloat((longV / shortV).toFixed(3)),
                long_short_volume_sum: longV - shortV,

                holding_long_short_volume_sum: HLongV + HShortV,
                btc_alt_volume_ratio: parseFloat((Math.abs(btcV) / Math.abs(altV)).toFixed(3)),
                bitcoin: charts[key][1] ? charts[key][1].toFixed(0) : 0,
                name: moment.utc(data[key].day, 'YYYYMMDD').local().format('YYYY/MM/DD'),
            }
            longC = 0
            longV = 0
            shortV = 0
            shortC = 0
            altV = 0
            btcV = 0
            HLongV = 0
            HShortV = 0
            row['long_short_count_ratio'] = parseFloat((longC / shortC).toFixed(3))
            row['long_short_count_sum'] = longC - shortC
            row['long_short_volume_ratio'] = parseFloat((longV / shortV).toFixed(3))
            row['long_short_volume_sum'] = longV - shortV
            row['holding_long_short_volume_sum'] = HLongV + HShortV
            row['btc_alt_volume_ratio'] = parseFloat((Math.abs(btcV) / Math.abs(altV)).toFixed(3))

            csvData.push(set)
            dataset.push(set)
        }

        return {dataset, title: group, headers, csvData}
    }, [data, charts, group])

    useEffect(() => {
        refetch()
        rebit()
    }, [days, rebit, refetch])

    return (
        <ContainerLayout title={condition ? `${Igroup} (${condition})` : `${Igroup} `} className={'block'}>
            <div className="flex gap-[10px] justify-end w-full">
                <CSVLink data={csvData} headers={headers} filename={`${Igroup}_data.csv`}>
                    <WizButton text={'export csv'} className="mb-5 mr-5" />
                </CSVLink>
            </div>
            <div className="z-0 flex flex-col">
                <div className="grid grid-cols-2 w-full h-full p-[30px] border-double border-2 border-grey-500">
                    <CComposedChart dataset={dataset} title={title} />
                </div>
            </div>
        </ContainerLayout>
    )
}

export default ServerAnalysisContainer
